package dev.lingvo.site.pages.app.projects.slug.settings

import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.ProjectProviderMode
import dev.lingvo.shared.ProviderSettingsDto
import dev.lingvo.shared.UpdateProviderSettingsRequest
import kotlinx.coroutines.CancellationException

/** Kept in the same order as the pinned server validation contract. */
internal val CLAUDE_PROJECT_MODELS = listOf(
    "claude-opus-5",
    "claude-sonnet-5",
    "claude-haiku-4-5",
)

/** Frontend-only intent kept separate from the public wire DTO's omit/null/value schema. */
internal data class ProjectProviderUpdateCommand(
    val request: UpdateProviderSettingsRequest,
    val clearBaseUrl: Boolean = false,
    val clearModel: Boolean = false,
)

internal fun providerUpdateRequest(
    provider: MtProvider,
    mode: ProjectProviderMode,
    apiKey: String,
    model: String,
    baseUrl: String,
    storedModel: String? = null,
    storedBaseUrl: String? = null,
): ProjectProviderUpdateCommand {
    val storedModelValue = providerModelValue(provider, storedModel.orEmpty())
    val draftModelValue = providerModelValue(provider, model)
    val modelChanged = draftModelValue != storedModelValue
    val nextModel = normalizedProviderModel(provider, model)
        .takeIf { mode == ProjectProviderMode.OVERRIDE && modelChanged }
    val nextBaseUrl = normalizedProviderBaseUrl(provider, baseUrl).takeIf { mode == ProjectProviderMode.OVERRIDE }
    return ProjectProviderUpdateCommand(
        request = UpdateProviderSettingsRequest(
            mode = mode,
            // Blank ordinary key input is deliberately "keep". Clearing the secret remains a separate
            // confirmed operation; optional non-secret settings can safely expose a Default choice.
            apiKey = apiKey.takeIf { mode == ProjectProviderMode.OVERRIDE && it.isNotBlank() },
            model = nextModel,
            baseUrl = nextBaseUrl,
        ),
        clearModel = mode == ProjectProviderMode.OVERRIDE && modelChanged &&
            storedModelValue != null && draftModelValue == null,
        clearBaseUrl = mode == ProjectProviderMode.OVERRIDE &&
            normalizedProviderBaseUrl(provider, storedBaseUrl.orEmpty()) != null && nextBaseUrl == null,
    )
}

internal fun clearStoredProviderKeyRequest(): ProjectProviderUpdateCommand =
    ProjectProviderUpdateCommand(
        request = UpdateProviderSettingsRequest(mode = ProjectProviderMode.INHERIT, apiKey = ""),
    )

internal fun ProviderSettingsDto.hasUsableStoredProjectKey(): Boolean =
    !keyUnreadable && (hasKey || hasStoredProjectKey)

internal fun ProviderSettingsDto.hasStoredProjectCredential(): Boolean =
    hasKey || hasStoredProjectKey || keyUnreadable

internal fun libreEndpointCredentialConfirmationRequired(
    settings: ProviderSettingsDto,
    baseUrl: String,
    apiKey: String,
): Boolean = settings.provider == MtProvider.LIBRETRANSLATE &&
    settings.hasStoredProjectCredential() &&
    normalizedProviderBaseUrl(settings.provider, baseUrl) !=
    normalizedProviderBaseUrl(settings.provider, settings.baseUrl.orEmpty()) &&
    apiKey.isBlank()

internal fun providerDraftDirty(
    settings: ProviderSettingsDto,
    mode: ProjectProviderMode,
    apiKey: String,
    model: String,
    baseUrl: String,
): Boolean = mode != settings.mode || apiKey.isNotBlank() ||
    providerModelValue(settings.provider, model) != providerModelValue(settings.provider, settings.model.orEmpty()) ||
    normalizedProviderBaseUrl(settings.provider, baseUrl) !=
    normalizedProviderBaseUrl(settings.provider, settings.baseUrl.orEmpty())

private fun normalizedProviderModel(provider: MtProvider, value: String): String? = value.trim()
    .takeIf { provider == MtProvider.CLAUDE && it in CLAUDE_PROJECT_MODELS }

private fun providerModelValue(provider: MtProvider, value: String): String? = value.trim()
    .takeIf { provider == MtProvider.CLAUDE && it.isNotEmpty() }

private fun normalizedProviderBaseUrl(provider: MtProvider, value: String): String? = value.trim()
    .takeIf { provider == MtProvider.LIBRETRANSLATE && it.isNotEmpty() }
